import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Backdrop, Box, CircularProgress, ClickAwayListener, Fade, Popper, Typography, FormControl } from '@material-ui/core';
import { useHistory } from 'react-router';
import MenuItem from '@material-ui/core/MenuItem';
import ResultWithAddress from './components/ResultWithAddress';
import SearchFilters from './components/SearchFilters';
import SearchInput from './components/SearchInput';
import WorkerResult from './components/WorkerResult';
import StatusChip from 'modules/views/shared/StatusChip';
import { GeneralModel, ResourceModel, SearchModel, UserModel } from 'modules/models';
import { useDebounce } from 'utils/useDebounce';
import { useHideScroll } from 'utils/useHideScroll';
import { isEmpty } from 'utils/generalUtils';
import { useStyles } from './styles';
import { getClients, getDrawerButton as getClientDrawerButton, getClientLabel } from 'utils/clientUtils';
import { useStyles as statusChipStyles } from 'modules/views/shared/StatusChip/styles';
import { ROUTES } from 'constants/routes';

const MIN_SEARCH_LENGHT = 3;
const PAGE_SIZE = 30;
export const quickSearchFilterTypeKey = 'quickSearchFilterType';

export const searchValueToPermissionMapper: {
  [key: string]: { permissionsExpression: string; includeScopedPermissions?: boolean; shouldBeFcaUser?: boolean };
} = {
  [SearchModel.SearchType.Project]: ROUTES.PROJECT_LIST,
  [SearchModel.SearchType.Company]: ROUTES.CLIENT_LIST,
  [SearchModel.SearchType.Worker]: ROUTES.WORKER_LIST,
  [SearchModel.SearchType.Invoice]: ROUTES.INVOICE_DETAIL,
  [SearchModel.SearchType.Users]: {
    permissionsExpression: UserModel.UsersPermission.VIEWACCESS,
    includeScopedPermissions: true,
    shouldBeFcaUser: false,
  },
};
export interface IProps {
  clearSearch: () => void;
  loading: GeneralModel.ILoadingStatus;
  loadingMore: GeneralModel.ILoadingStatus;
  searchResults?: GeneralModel.IPagination<SearchModel.IResponse | SearchModel.IWorker>;
  triggerSearch: (search: SearchModel.ISearchParams) => void;
  triggerSearchMore: (search: SearchModel.ISearchParams) => void;
  isFcaUser: boolean;
  isAdmin: boolean;
  clientBranchesMap?: any;
  fetchClientBranches?: (id: string, query: GeneralModel.IQueryParams) => void;
  clientBranchesLoading?: GeneralModel.ILoadingStatus;
  currentUserPermissions: UserModel.IPermission[];
}

const Search = ({
  clearSearch,
  loading,
  loadingMore,
  searchResults,
  triggerSearch,
  triggerSearchMore,
  isFcaUser,
  isAdmin,
  clientBranchesMap,
  fetchClientBranches,
  clientBranchesLoading,
  currentUserPermissions,
}: IProps) => {
  // TODO: Remove this with a fix for the input styles
  // This is a workaround to keep styles import in order.
  // eslint-disable-next-line
  const useRefFormControl = useRef(FormControl);
  const history = useHistory();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { setHideScroll } = useHideScroll();

  const hasMorePages = useMemo<boolean>(() => searchResults?.totalResults === PAGE_SIZE, [searchResults]);

  const hasPermissions = useMemo(() => {
    const workerFilterPermission = searchValueToPermissionMapper[SearchModel.SearchType.Worker];
    const projectFilterPermission = searchValueToPermissionMapper[SearchModel.SearchType.Project];
    const clientFilterPermission = searchValueToPermissionMapper[SearchModel.SearchType.Company];
    const userFilterPermission = searchValueToPermissionMapper[SearchModel.SearchType.Users];
    const invoiceFilterPermission = searchValueToPermissionMapper[SearchModel.SearchType.Invoice];

    return {
      [SearchModel.SearchType.Worker]:
        (!workerFilterPermission.shouldBeFcaUser || isFcaUser) &&
        UserModel.hasValidPermissions(
          workerFilterPermission?.permissionsExpression,
          currentUserPermissions,
          '',
          '',
          workerFilterPermission?.includeScopedPermissions
        ),
      [SearchModel.SearchType.Project]:
        (!projectFilterPermission.shouldBeFcaUser || isFcaUser) &&
        UserModel.hasValidPermissions(
          projectFilterPermission?.permissionsExpression,
          currentUserPermissions,
          '',
          '',
          projectFilterPermission?.includeScopedPermissions
        ),
      [SearchModel.SearchType.Company]:
        (!clientFilterPermission.shouldBeFcaUser || isFcaUser) &&
        UserModel.hasValidPermissions(
          clientFilterPermission?.permissionsExpression,
          currentUserPermissions,
          '',
          '',
          clientFilterPermission?.includeScopedPermissions
        ),
      [SearchModel.SearchType.Users]:
        (!userFilterPermission.shouldBeFcaUser || isFcaUser) &&
        UserModel.hasValidPermissions(
          userFilterPermission?.permissionsExpression,
          currentUserPermissions,
          '',
          '',
          userFilterPermission.includeScopedPermissions
        ),
      [SearchModel.SearchType.Invoice]:
        (!invoiceFilterPermission.shouldBeFcaUser || isFcaUser) &&
        UserModel.hasValidPermissions(
          invoiceFilterPermission?.permissionsExpression,
          currentUserPermissions,
          '',
          '',
          invoiceFilterPermission?.includeScopedPermissions
        ),
    };
  }, [currentUserPermissions, isFcaUser]);

  const hasAnyActiveFilterPermission = useMemo(() => {
    return SearchModel.searchFiltersConfig.reduce((hasPermission, f) => hasPermission || (f.active && hasPermissions[f.value]), false);
  }, [currentUserPermissions, hasPermissions]);

  const getUpdatedSelectedFilterValue = useCallback(
    selectedValue => {
      let newSelectedValue = selectedValue;
      if (newSelectedValue && hasPermissions[newSelectedValue] && SearchModel.searchFiltersConfig.find(f => f.value === newSelectedValue)?.active) {
        newSelectedValue = newSelectedValue;
      } else {
        newSelectedValue = null;
      }

      if (!newSelectedValue) {
        for (let i = 0; i < SearchModel.searchFiltersConfig.length; i++) {
          if (hasPermissions[SearchModel.searchFiltersConfig[i].value] && SearchModel.searchFiltersConfig[i].active) {
            newSelectedValue = SearchModel.searchFiltersConfig[i].value;
            localStorage.setItem(quickSearchFilterTypeKey, String(newSelectedValue));
            break;
          }
        }
      }
      return newSelectedValue;
    },
    [hasPermissions]
  );

  const getDefaultSelectedFilterValue = useCallback(() => {
    let selectedValue = Number(localStorage.getItem(quickSearchFilterTypeKey) || SearchModel.SearchType.Worker);
    return getUpdatedSelectedFilterValue(selectedValue);
  }, [hasPermissions, getUpdatedSelectedFilterValue]);

  const [isActive, setIsActive] = useState<boolean>(false);

  const classes = useStyles({ isActive });
  const [selectedFilter, setSelectedFilter] = useState<SearchModel.SearchType>(getDefaultSelectedFilterValue());
  const selectedFilterName = SearchModel.filterLabelsMap[selectedFilter];
  const handleFilterChange = (newFilter: SearchModel.SearchType) => {
    setSelectedFilter(newFilter);
    localStorage.setItem(quickSearchFilterTypeKey, String(newFilter));
    inputRef?.current?.focus();
  };
  const searchFilters = useMemo(
    () =>
      SearchModel.searchFiltersConfig
        .filter(filter => filter.active && filter.value !== SearchModel.SearchType.Users && filter.value !== SearchModel.SearchType.Invoice)
        .map(filter => ({ ...filter, onClick: () => handleFilterChange(filter.value) })),
    [isFcaUser]
  );
  const statusChipClasses = statusChipStyles();
  const [search, setSearch] = useState<string>();
  const [branches, setBranches] = useState({});
  const debouncedSearch = useDebounce(search ? encodeURIComponent(search) : search, 350);
  const handleSearchChange = (value: string) => setSearch(value);
  const handleClearSearch = () => {
    setSearch(null);
    inputRef?.current?.focus();
  };
  const handleClose = () => {
    if (clientForBranches) {
      setClientForBranches(null);
      document.getElementsByTagName('html')[0].style.overflow = '';
      document.body.style.paddingRight = '';
    } else {
      setIsActive(false);
      setSearch(null);
    }
  };

  useEffect(() => {
    const newFilterValue = getUpdatedSelectedFilterValue(selectedFilter);
    if (selectedFilter !== newFilterValue) {
      setSelectedFilter(newFilterValue);
    }
  }, [hasPermissions, selectedFilter]);

  useEffect(() => {
    clearSearch();
    /* istanbul ignore else */
    if (isActive && debouncedSearch && debouncedSearch.length >= MIN_SEARCH_LENGHT) {
      triggerSearch({ nameContains: debouncedSearch, searchType: selectedFilter, pageSize: PAGE_SIZE });
    }
  }, [clearSearch, debouncedSearch, isActive, selectedFilter, triggerSearch]);

  // Close search on scape press
  useEffect(() => {
    const scapeKeyListener = (event: KeyboardEvent) => {
      /* istanbul ignore else */
      if (event.key === 'Escape') {
        inputRef?.current?.blur();
        handleClose();
      }
    };
    setHideScroll(isActive, 'hidden'); // prevent scroll when dialog is open
    if (isActive) {
      window.addEventListener('keyup', scapeKeyListener);
    } else {
      window.removeEventListener('keyup', scapeKeyListener);
    }
    return () => {
      window.removeEventListener('keyup', scapeKeyListener);
      setHideScroll(false);
    };
  }, [isActive, setHideScroll]);

  useEffect(() => {
    if (clientBranchesMap && clientBranchesMap.id && clientBranchesMap.hierarchy) {
      let tempBranches = { ...branches };
      let tempClients = getClients(clientBranchesMap.hierarchy);
      if (tempClients && tempClients.length > 0) {
        tempClients = tempClients.filter(c => c.id !== clientBranchesMap.id);
      }
      tempBranches[clientBranchesMap.id] = tempClients;
      setBranches(tempBranches);
    }
  }, [clientBranchesMap]);

  const getBranches = useCallback(
    (id: string) => {
      if (!branches[id]) {
        fetchClientBranches(id, {});
      }
    },
    [branches, fetchClientBranches]
  );

  const [clientForBranches, setClientForBranches] = useState(null);
  const cBranchesCnrRef: any = useRef();
  useEffect(() => {
    if (clientForBranches && clientForBranches.id) {
      const element = document.getElementById('co-' + clientForBranches.id);
      if (element) {
        const offsets = element.getBoundingClientRect?.() || ({} as any);
        let top = offsets.top;
        if (top > window.innerHeight) {
          top = window.innerHeight;
        }
        const windowHeight = window.innerHeight / 2;
        cBranchesCnrRef.current.style.left = offsets.left - 70 + 'px';
        if (top <= windowHeight) {
          cBranchesCnrRef.current.style.top = top - 70 + 'px';
          // cBranchesCnrRef.current.style.top = top - 30 + 'px';
          cBranchesCnrRef.current.style.bottom = '';
        } else {
          cBranchesCnrRef.current.style.top = '';
          cBranchesCnrRef.current.style.bottom = window.innerHeight - top + 'px';
        }
        cBranchesCnrRef.current.style.boxShadow = '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)';
        document.getElementsByTagName('html')[0].style.overflow = 'hidden';
        document.body.style.paddingRight = '17px';
      }
    }
  }, [clientForBranches]);

  useEffect(() => {
    if (clientForBranches && clientForBranches.id && !branches[clientForBranches.id]) {
      getBranches(clientForBranches.id);
    }
  }, [clientForBranches]);

  const onSearchResultsScroll = useCallback(
    e => {
      const isBottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
      /* istanbul ignore else */
      if (isActive && isBottom && hasMorePages && !loadingMore?.isLoading) {
        triggerSearchMore({ nameContains: debouncedSearch, searchType: selectedFilter, pageNumber: searchResults?.pageNumber + 1, pageSize: PAGE_SIZE });
      }
    },
    [hasMorePages, isActive, loadingMore, debouncedSearch, searchResults, selectedFilter, triggerSearchMore]
  );

  const renderLoading = useCallback(
    () => (
      <Box className={classes.loadingIndicatorWrapper}>
        <CircularProgress title="Loading..." />
      </Box>
    ),
    [classes.loadingIndicatorWrapper]
  );

  const renderSearchResults = useCallback(() => {
    if (search?.length < MIN_SEARCH_LENGHT) {
      return <Typography className={classes.emptyResultInfo}>Insert at least {MIN_SEARCH_LENGHT} characters</Typography>;
    } else if (loading?.hasError) {
      return <Typography className={`${classes.emptyResultInfo} ${classes.error}`}>There was an error. Please try again.</Typography>;
    } else if (searchResults && (!searchResults?.items || searchResults?.items?.length === 0)) {
      return <Typography className={classes.emptyResultInfo}>Your search did not match any {selectedFilterName}</Typography>;
    } else {
      const renderResult = (item: SearchModel.IResponse | SearchModel.IWorker) => {
        switch (item.entityType) {
          case SearchModel.SearchType.Company:
            return <ResultWithAddress key={item.id} onClick={handleClose} item={item} setClientForBranches={setClientForBranches} />;
          case SearchModel.SearchType.Project:
            return <ResultWithAddress key={item.id} onClick={handleClose} item={item} />;
          case SearchModel.SearchType.Worker:
            return <WorkerResult key={item.id} onClick={handleClose} worker={item as SearchModel.IWorker} />;
          /* istanbul ignore next */
          default:
            return null;
        }
      };
      return (
        <Box className={classes.searchResults}>
          {searchResults?.items?.map(renderResult)}
          {loadingMore?.isLoading && <Box className={classes.searchMoreLoadingWrapper}>{renderLoading()}</Box>}
          {!loadingMore?.isLoading && !hasMorePages && !isEmpty(searchResults?.items) && (
            <Typography className={classes.noMoreItemsLabel}>No more results</Typography>
          )}
        </Box>
      );
    }
  }, [classes, hasMorePages, loading, loadingMore, renderLoading, search, searchResults, selectedFilterName]);

  return hasAnyActiveFilterPermission ? (
    <>
      <Backdrop open={isActive} className={classes.backdrop} onClick={handleClose}>
        {' '}
      </Backdrop>
      {/* <ClickAwayListener onClickAway={handleClose}> */}
      <div className={classes.wrapper} ref={wrapperRef}>
        <SearchInput
          chipLabel={selectedFilterName}
          inputRef={inputRef}
          isActive={isActive}
          search={search}
          handleClear={handleClearSearch}
          onChange={e => {
            if (clientForBranches) {
              setClientForBranches(null);
            }
            if (selectedFilter > SearchModel.SearchType.NotSet) {
              handleSearchChange(e);
            }
          }}
          onClick={() => hasAnyActiveFilterPermission && setIsActive(true)}
          onClose={handleClose}
          disabled={!hasAnyActiveFilterPermission}
        />
        {wrapperRef.current && (
          <Popper
            anchorEl={wrapperRef.current}
            id="searchInfo"
            open={isActive}
            transition={true}
            style={{ zIndex: 10100, maxWidth: wrapperRef?.current?.clientWidth + 1.5, width: '100%' }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={100}>
                <Box className={classes.searchModalContainer} onScroll={onSearchResultsScroll} data-testid="search-results">
                  {!search && <SearchFilters filters={searchFilters} selected={selectedFilter} />}
                  {search && loading?.isLoading && renderLoading()}
                  {search && !loading?.isLoading && (
                    <>
                      <Typography className={classes.searchResultsInfo}>Search Results</Typography>
                      {renderSearchResults()}
                    </>
                  )}

                  {isActive && debouncedSearch && debouncedSearch.length >= MIN_SEARCH_LENGHT && clientForBranches && clientForBranches.id && (
                    <>
                      <div role="presentation" className="MuiPopover-root" style={{ position: 'fixed', zIndex: 1100 }}>
                        <span ref={cBranchesCnrRef} style={{ position: 'fixed', zIndex: 9999, backgroundColor: '#FFFFFF', borderRadius: '10px' }}>
                          <MenuItem
                            style={{ width: '100%', fontWeight: 'bold', borderBottom: '1px solid #CCCCCC' }}
                            onClick={() => {
                              handleClose();
                              history.push(getClientDrawerButton(clientForBranches.status, clientForBranches.id).linkTo);
                            }}
                          >
                            {clientForBranches.name}
                          </MenuItem>
                          {clientBranchesLoading && clientBranchesLoading.isLoading ? (
                            <span style={{ display: 'block', margin: '15px' }}>
                              <CircularProgress />
                            </span>
                          ) : (
                            <div style={{ maxHeight: '47vh', overflow: 'auto' }}>
                              {branches && branches[clientForBranches.id] && branches[clientForBranches.id].length > 0 ? (
                                branches[clientForBranches.id].map((b: any) => {
                                  const branchLink = getClientDrawerButton(b.status, b.id);
                                  return (
                                    <MenuItem
                                      key={b.id}
                                      style={{ display: 'flex', justifyContent: 'space-between' }}
                                      onClick={() => {
                                        handleClose();
                                        history.push(branchLink.linkTo);
                                      }}
                                    >
                                      <span style={{ marginRight: '10px' }}>
                                        <span>{b.name}</span>
                                        <span style={{ fontWeight: 'bold', marginLeft: '10px' }}>{getClientLabel(b.companyLabel, false)}</span>
                                      </span>
                                      <span>
                                        <StatusChip
                                          label={ResourceModel.statusMap[b.status]}
                                          styleClasses={statusChipClasses[ResourceModel.statusMap[b.status]?.replace(/ /g, '').toLowerCase()]}
                                        />
                                      </span>
                                    </MenuItem>
                                  );
                                })
                              ) : (
                                <MenuItem>
                                  <span>No branches found.</span>
                                </MenuItem>
                              )}
                            </div>
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </Box>
              </Fade>
            )}
          </Popper>
        )}
      </div>
      {/* </ClickAwayListener> */}
    </>
  ) : null;
};

export default Search;
